import { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { supabase } from '../../../supabase/client';
import { pullFromCache, saveToCache, updateCache } from './messageCache';
import { incrementOperation, decrementOperation, addUpload, updateUpload, updateAttachment, removeUpload } from '../../../Redux/store';
import { generateImageUuid } from '../../../utils/imageUrls';

function useMessages(chatId, currentUserId) {
  const dispatch = useDispatch();
  const uploads = useSelector((s) => s.uploads.byId);
  const chats = useSelector((s) => s.chats.chats);
  const [messages, setMessages] = useState([]);
  const mountedRef = useRef(true);
  const uploadsRef = useRef(uploads);
  const loadingRef = useRef(false);

  // Get chat object
  const chat = chatId ? chats[chatId] : null;

  // Sync uploads to ref on every render to avoid closure trap
  useEffect(() => {
    uploadsRef.current = uploads;
  }, [uploads]);

  const loadMessages = async () => {
    // Try to load from cache first (instant, no loading indicator)
    const cachedMessages = pullFromCache(chatId);
    if (cachedMessages) {
      setMessages(cachedMessages);
    }
    
    // Load from Supabase with global loading indicator
    loadingRef.current = true;
    dispatch(incrementOperation('messages'));
    
    try {
      const { data, error } = await supabase
        .from('messages')
        .select('*')
        .eq('chat_id', chatId)
        .order('created_at', { ascending: true });
      
      if (data && mountedRef.current) {
        setMessages(data);
        saveToCache(chatId, data);
      }
    } finally {
      loadingRef.current = false;
      if (mountedRef.current) {
        dispatch(decrementOperation('messages'));
      }
    }
  };

  useEffect(() => {
    if (chatId) {
      loadMessages();
      // Mark chat as read when user enters
      supabase.rpc('mark_chat_read', {
        p_chat_id: chatId,
        p_user_id: currentUserId
      }).then(({ error }) => {
        if (error) {
        }
      });
    }
    
    return () => {
      mountedRef.current = false;
      // Clean up loading state if component unmounts while loading
      if (loadingRef.current) {
        dispatch(decrementOperation('messages'));
      }
    };
  }, [chatId, currentUserId]);

  // Realtime: listen for new messages and updates in this chat
  useEffect(() => {
    if (!chatId) return;

    // Remove any existing channel with the same name before creating a new one
    const existingChannel = supabase.getChannels().find(c => c.topic === `realtime:messages_${chatId}`);
    if (existingChannel) {
      supabase.removeChannel(existingChannel);
    }

    const channel = supabase
      .channel(`messages_${chatId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'messages',
          filter: `chat_id=eq.${chatId}`,
        },
        (payload) => {
          // Check if this message matches a completed upload and remove it
          const matchingUpload = Object.values(uploadsRef.current).find(
            upload => upload.realMessageId === payload.new.id
          );
          if (matchingUpload) {
            dispatch(removeUpload(matchingUpload.id));
          }

          // Add new message if not duplicate
          setMessages(prev => {
            if (prev.some(m => m.id === payload.new.id)) {
              return prev;
            }
            updateCache(chatId, [payload.new]);
            return [...prev, payload.new];
          });

          // Mark chat as read since user had it open
          supabase.rpc('mark_chat_read', {
            p_chat_id: chatId,
            p_user_id: currentUserId
          }).then(({ error }) => {
            if (error) {
            }
          });
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'messages',
          filter: `chat_id=eq.${chatId}`,
        },
        (payload) => {
          // Update message in state (for read receipts)
          setMessages(prev => {
            const updated = prev.map(m =>
              m.id === payload.new.id ? payload.new : m
            );
            updateCache(chatId, [payload.new]);
            return updated;
          });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [chatId, currentUserId]);

  const handleSendMessage = async (body, attachments = []) => {
    const uploadId = `upload-${Date.now()}`;
    const createdAt = new Date().toISOString();

    // Get other user ID from chat (for calendar share logic)
    const otherUserId = chat?.user_ids?.find(id => id !== currentUserId);

    // Prepare attachments array for upload
    const uploadAttachments = attachments.map(att => {
      if (att.type === 'calendar' || att.type === 'calendar_share' || att.type === 'calendar_request') {
        // For calendar attachments, requesterId is already set in ChatSender
        return {
          id: att.id,
          type: att.type,
          status: 'ready',
          data: att.data,
        };
      }
      
      // For all image attachments (both file and camera), upload at send time
      return {
        id: att.id,
        type: att.type,
        status: 'pending',
        file: att.file,
        url: null,
        progress: 0,
        preview: att.preview || (att.file ? URL.createObjectURL(att.file) : null),
        imageData: att.imageData, // Store metadata (source: 'camera' or null for file uploads)
        loading: true,
      };
    });

    // Add to Redux upload tracker
    dispatch(addUpload({
      id: uploadId,
      type: 'message',
      status: 'uploading',
      progress: 0,
      attachments: uploadAttachments,
      data: { body },
      chatId,
      senderId: currentUserId,
      createdAt,
    }));

    // Upload files in parallel to Supabase storage
    // Each image gets a unique UUID and is stored at path: userId/uuid
    // Upload progress is tracked via Redux updates
    const imageAttachments = uploadAttachments.filter(a => a.type === 'image');
    let uploadedFiles = [];
    try {
      const uploadPromises = imageAttachments.map(async (attachment) => {
        dispatch(updateAttachment({
          uploadId,
          attachmentId: attachment.id,
          status: 'uploading',
          progress: 0,
        }));

        // Generate UUID for the file
        const fileUuid = generateImageUuid();
        const filePath = `${currentUserId}/${fileUuid}`;

        // Upload to Supabase storage
        const { error: uploadError } = await supabase.storage
          .from('chat-images')
          .upload(filePath, attachment.file, {
            cacheControl: '3600',
            upsert: false,
          });

        if (uploadError) {
          console.error('[Image Upload] Upload error:', uploadError);
          throw uploadError;
        }
        dispatch(updateAttachment({
          uploadId,
          attachmentId: attachment.id,
          status: 'complete',
          url: filePath, // Store full path (userId/uuid)
          progress: 100,
        }));

        // Return JSONB object with metadata (loading: false since upload is complete)
        // Use source from imageData if available (camera vs upload)
        const metadata = {
          id: attachment.id,
          imageUUID: fileUuid,
          source: attachment.imageData?.source || 'upload',
          userId: currentUserId,
          bucket: 'chat-images',
          loading: false
        };
        return metadata;
      });

      uploadedFiles = await Promise.all(uploadPromises);
    } catch (error) {
      const errorMessage = error.message?.includes('NetworkError')
        ? 'No Internet. Tap to retry'
        : error.message || 'Failed to upload files';
      dispatch(updateUpload({
        id: uploadId,
        status: 'error',
        error: errorMessage,
      }));
      return;
    }

    // Prepare message data with attachments (JSONB array with metadata)
    const imageUrls = uploadedFiles.map(f => ({
      imageUUID: f.imageUUID,
      source: f.source,
      userId: f.userId,
      bucket: f.bucket,
      loading: false // Upload complete, no longer loading
    }));

    // Extract calendar attachments with proper structure
    const calendarAttachments = uploadAttachments
      .filter(a => a.type === 'calendar' || a.type === 'calendar_share' || a.type === 'calendar_request')
      .map(a => ({
        type: a.type,
        data: a.data,
      }));

    const { data, error } = await supabase.rpc('send_message_j', {
      p_chat_id: chatId,
      p_sender_id: currentUserId,
      p_body: body,
      p_image_urls: imageUrls,
      p_attachments: calendarAttachments.length > 0 ? calendarAttachments : null,
    });

    if (error) {
      console.error('[Image Upload] RPC error details:', error.message, error.details, error.hint);
      const errorMessage = error.message?.includes('NetworkError')
        ? 'No Internet. Tap to retry'
        : error.message || 'Failed to send message';
      dispatch(updateUpload({
        id: uploadId,
        status: 'error',
        error: errorMessage,
      }));
      return;
    }

    dispatch(updateUpload({
      id: uploadId,
      status: 'complete',
      realMessageId: data,
    }));

    // Realtime will add the real message and remove the upload
  };

  const retryUpload = async (uploadId) => {
    const upload = uploads[uploadId];
    if (!upload) {
      return;
    }

    // Update status to uploading
    dispatch(updateUpload({
      id: uploadId,
      status: 'uploading',
      error: null,
    }));

    const { data, error } = await supabase.rpc('send_message_j', {
      p_chat_id: upload.chatId,
      p_sender_id: upload.senderId,
      p_body: upload.data.body,
    });

    if (error) {
      const errorMessage = error.message?.includes('NetworkError')
        ? 'No Internet. Tap to retry'
        : error.message || 'Failed to send message';
      dispatch(updateUpload({
        id: uploadId,
        status: 'error',
        error: errorMessage,
      }));
      return;
    }

    dispatch(updateUpload({
      id: uploadId,
      status: 'complete',
      realMessageId: data,
    }));

    // Realtime will add the real message and remove the upload
  };

  return { messages, handleSendMessage, retryUpload, chat };
}

// TODO: Implement scroll-to-top load more functionality
// Current implementation: Messages are loaded in full from cache/Supabase on chat open
// Future: When user scrolls to top, load older messages from Supabase and prepend to state
// This will require pagination logic and updating the cache accordingly

export default useMessages;
